<template>
  <b-table
    :sort-by.sync="localPreSort"
    :sort-compare="sortCompare"
    :ref="tableReference"
    :filter-function="filterFunction"
    @filtered="onFiltered"
    hover
    small
    :filter-included-fields="filterOn"
    :sort-desc="true"
    striped
    :filter="filterRows"
    :current-page="currentPage"
    :per-page="perPage"
    thead-class="position-sticky"
    :items="model_items"
    :fields="filteredFields"
    head-variant="dark"
    :tbody-tr-class="rowClass"
  >
    <!-- HEADERS -->
    <template #head(checkbox)>
      <b-form-group>
        <b-form-checkbox
          size="lg"
          @change="$emit('selectAllRows', $event)"
          v-model="allRowsSelected"
          variant="danger"
          class="mr-1 ml-2 _red-checkbox"
        >
        </b-form-checkbox>
      </b-form-group>
    </template>

    <template #head(previous_net_price)>
      <div class="d-flex justify-content-center align-items-center">
        <div style="white-space: nowrap">
          Net Price
          <svg width="25px" height="25px" viewBox="0 0 10 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 1.5L0.5 13.5H10.0L5.3 1.5Z" stroke="#fff" stroke-linejoin="round" />
          </svg>
        </div>
      </div>
    </template>

    <template #head(favorite)>
      <font-awesome-icon :icon="['fas', 'star']" />
    </template>

    <template #head(customProjectName)="data">
      <button @click="$emit('refreshResults')" class="btn btn-light ml-3">UPDATE</button>
      {{ data.label }}
    </template>

    <template #head(mls_address)="data">
      <button @click="$emit('refreshResults', listingType)" class="btn btn-light btn-sm mx-0 p-1" style="white-space: nowrap; margin: 0 15px" size="sm">
        UPDATE
      </button>
      {{ data.label }}
    </template>

    <template #head(updatemls)>
      <div v-if="showFlag" style="font-size: larger">Update MLS</div>
    </template>

    <!-- CELLS -->
    <template #cell(favorite)="data">
      <font-awesome-icon
        @click="$emit('toggleFavorite', data.item)"
        :icon="data.value ? ['fas', 'star'] : ['far', 'star']"
        class="cursor-pointer"
        style="cursor: pointer"
      />
    </template>

    <template #cell(mls_list_price)="rowData">
      <span v-if="Number(rowData.value) > 0" v-b-modal="'mlspop'" @click="$emit('popHistory', rowData.item)">
        <span :style="{ color: setColor(getPriceBackupKey(rowData.item)) }">
          ${{ Math.round(rowData.value).toLocaleString() }}
        </span>
      </span>
      <span v-else>-</span>
    </template>

    <template #cell(sales_start_date)="data">
      <span v-if="data.value">{{ data.value }}</span>
      <span class="font-italic" v-else>Previewing</span>
    </template>

    <template #cell(custom_price_confidence)="data">
      <span :class="{ 'font-weight-bold': data.value != '-' }" :style="{ color: setConfidenceColor(data.value) }">
        {{ data.value != "-" ? data.value : "-" }}
      </span>
    </template>

    <template #cell(custom_status)="data">
      <span :class="{ 'text-danger': ['Leased', 'Sold'].includes(data.value), 'text-success': data.value == 'Active' }">
        {{ data.value }}
      </span>
    </template>

    <template #cell(custom_listing_type)="data">
      <BBadge v-if="['resale', 're-lease'].includes(data.value.toLowerCase())" variant="vb-red">{{ data.value }}</BBadge>
      <BBadge v-else-if="['assignment'].includes(data.value.toLowerCase())" variant="warning">{{ data.value }}</BBadge>
      <BBadge v-else variant="vb-secondary">{{ data.value }}</BBadge>
    </template>

    <template #cell(custom_mls_net_psf)="data">
      <span>{{ data.value != "-" ? `$${data.value}` : "-" }}</span>
    </template>

    <template #cell(custom_strata_fee)="rowData">
      <span v-if="Number(rowData.value) > 0" v-b-modal="`mls-${rowData.item.mls_status}-` + rowData.item.cid">
        ${{ Math.round(rowData.value).toLocaleString() }}
      </span>
      <span v-else>-</span>
    </template>

    <template #cell(custom_parking_stall_cost)="rowData">
      <span v-if="Number(rowData.value) > 0" v-b-modal="`mls-${rowData.item.mls_status}-` + rowData.item.cid">
        ${{ Math.round(rowData.value).toLocaleString() }}
      </span>
      <span v-else>-</span>
    </template>

    <template #cell(customPlanningData)="data">
      <template v-if="isInternalRole">
        <template v-for="(value, key) in filterPlanningData(data.value)">
          <p v-if="value" :key="key">{{ key }} : {{ value }}</p>
        </template>
      </template>
      <template v-else>
        <template v-for="(value, key) in data.value">
          <p v-if="value" :key="key">{{ key }} : {{ value }}</p>
        </template>
      </template>
    </template>

    <template #cell(project_name)="data">
      <h5 class="text-left pl-3">
        <a class="text-vb-primary" style="font-weight: 500; font-size: 20px" :href="data.item.link">{{ data.value }}</a>
      </h5>
    </template>

    <template #cell(customData)="data">
      <div class="row justify-content-between pl-2 pr-5">
        <div>
          <h5 class="text-left pl-3">
            <a class="text-vb-primary" style="font-weight: 500; font-size: 20px" :href="data.value.href_link">
              {{ data.value.projectName }}
              <font-awesome-icon :icon="['fas', 'medal']" v-if="isInCurrentQuarter(data.item.sales_start_date)" />
            </a>
          </h5>
          <p class="text-left pl-3 text-vb-primary mb-0 pb-0" style="font-weight: 400; font-size: 18px">{{ data.value.project }}</p>
        </div>
      </div>
    </template>

    <template #cell(customProjectName)="data">
      <div class="row justify-content-between pl-2 pr-5">
        <div>
          <h5 class="text-left pl-3">
            <a class="text-vb-primary" style="font-weight: 500; font-size: 20px" :href="data.value.href_link">
              {{ data.value.projectName }}
              <font-awesome-icon :icon="['fas', 'medal']" v-if="isSellingLeasingSoon(data.item.status)" />
            </a>
          </h5>
          <p class="text-left pl-3 text-vb-primary mb-0 pb-0" style="font-weight: 400; font-size: 18px">{{ data.value.project }}</p>
        </div>
      </div>
    </template>

    <template #cell(customTypeStoreyData)="data">
      <p class="pl-3 text-vb-primary mb-0 pb-0" style="font-weight: 400; font-size: 18px">{{ data.value.projectType }}</p>
      <p class="pl-3 text-vb-primary mb-0 pb-0" style="font-weight: 400; font-size: 18px">({{ data.value.storey ? data.value.storey : "-" }})</p>
    </template>

    <template #cell(customLocationData)="data">
      <p class="text-left pl-3 text-vb-primary mb-0 pb-0" style="font-weight: 400; font-size: 18px">{{ data.value.address }}</p>
      <p v-if="data.value.municipality" class="text-left pl-3 text-vb-primary mb-0 pb-0" style="font-weight: 400; font-size: 18px">
        {{ data.value.municipality }}
      </p>
      <p v-else class="text-left pl-3 text-vb-primary mb-0 pb-0" style="font-weight: 400; font-size: 18px">{{ data.value.neighbour }}</p>
    </template>

    <template #cell(field_mls_address_value)="data">
      <span v-if="data.item.field_mls_floor_plan_fid != null" style="cursor: pointer" class="text-primary" @click="$emit('getFloorPlan', data.item.field_mls_floor_plan_fid)">
        <u>{{ data.value }}</u>
      </span>
      <span v-else>{{ data.value }}</span>
    </template>

    <template #cell(field_mls_list_price_value)="data">
      <span v-b-modal="'mlspop'" @click="$emit('popHistory', data.item)" :style="{ color: setColor(data.item.price_backup) }">
        {{ data.value }}
      </span>
    </template>

    <template #cell(checkbox)="data">
      <div class="d-flex">
        <b-form-checkbox size="lg" variant="danger" v-model="data.item.rowSelected" @change="$emit('promptRow', data.item)" class="mr-1 _red-checkbox" />
        <span v-if="listingType == 'rental'" class="ml-2" @click="$emit('deleteMlsRentalRow', { rid: data.item.rid, rowData: data.item })">
          <font-awesome-icon :icon="['fas', 'trash-alt']" class="_cursor-pointer text-vb-red" />
        </span>
      </div>
    </template>

    <template #cell(updatemls)="data">
      <b-button v-if="(openTab != 'unitmixrental' || showFlag || isAdmin) && isMarketStrata" size="sm" variant="vb-secondary" class="btn-sm m-1 p-1" @click="$emit('updateMls', { cid: data.item.cid, unit: data.item.mls_address })">
        Update Rental
      </b-button>
      <a v-if="openTab != 'unitmixrental'" class="btn btn-sm btn-light m-1 p-1" :href="`https://www.realpropertydata.ca/backend/field-collection/field-mls-collection/${data.item.cid}/edit`" target="_blank">
        Edit
      </a>
    </template>

    <template #cell(architect_consultant)="data">
      <h4 v-if="data.value.length > 0">
        <BBadge variant="vb-secondary" class="m-1" v-for="(item, index) in data.value" :key="index">{{ item }}</BBadge>
      </h4>
      <p v-else>-</p>
    </template>

    <template #cell(marketing_consultant)="data">
      <h4 v-if="data.value.length > 0">
        <BBadge variant="vb-secondary" class="m-1" v-for="(item, index) in data.value" :key="index">{{ item }}</BBadge>
      </h4>
      <p v-else>-</p>
    </template>

    <template #cell(general_contractor)="data">
      <h4 v-if="data.value.length > 0">
        <BBadge variant="vb-secondary" class="m-1" v-for="(item, index) in data.value" :key="index">{{ item }}</BBadge>
      </h4>
      <p v-else>-</p>
    </template>

    <template #cell(custom_price_change)="data">
      <div v-if="data.value.diffPercentage != 0" v-b-modal="`mls-${listingType}-` + data.item.cid">
        <span class="font-weight-bold" :style="{ color: data.value.color }">
          {{ data.value.diffPercentage }}%
          <font-awesome-icon class="font-weight-bold" :icon="data.value.icon" />
        </span>
      </div>
      <div v-else>-</div>
    </template>

    <template #cell(mls_beds)="data">
      <template v-if="userRole !== 'admin'">
        <strong class="font-weight-bold" v-b-modal="`mls-${listingType}-` + data.item.cid" v-if="data.value == 's'">Studio</strong>
        <strong class="font-weight-bold" v-b-modal="`mls-${listingType}-` + data.item.cid" v-else>{{ data.item.mls_beds }}-bed</strong>
        <br />
        {{ data.item.fp_specs.toLowerCase() != "none" ? data.item.fp_specs : "" }}
      </template>
      <span v-else>{{ data.value }}</span>
    </template>

    <template #cell(custom_field_mls_beds_value)="data">
      <strong class="font-weight-bold" v-b-modal="`mls-${listingType}-` + data.item.cid" v-if="data.value.type">{{ data.value.type }}</strong>
      <br />
      {{ data.item.fp_specs.toLowerCase() != "none" ? data.item.fp_specs : "" }}
    </template>

    <template #cell(mls_address)="data">
      <u v-if="Object.entries(data.item.mls_floor_plan_feature).length > 0" class="text-primary" v-b-modal="'mls-fp-' + data.item.mls_floor_plan_feature.fid">
        {{ data.value }}
      </u>
      <span v-else>{{ data.value }}</span>
    </template>

    <template #cell(delete)="data">
      <button v-if="userRole == 'admin'" @click="$emit('deleteMLS', data.item.cid)" class="button border-0" style="background: none">
        <font-awesome-icon class="text-vb-red _cursor-pointer" :icon="['fas', 'trash']" />
      </button>
      <button v-else class="border-0 button" style="background: none" disabled>
        <font-awesome-icon class="text-muted" :icon="['fas', 'trash']" />
      </button>
    </template>

    <!-- Default catch-all cell -->
    <template #cell()="data">
      <span v-b-modal="`mls-${listingType}-` + data.item.cid" @click="$emit('popHistory', data.item)">
        {{ getDefaultValue(data).data }}
      </span>
      <span
        v-if="['custom_mls_net_rent', 'list_price_net'].includes(data.field.key) && data.item.custom_price_change.diffPercentage != 0"
        :style="{ color: data.item.custom_price_change.color }"
        class="font-weight-bold"
      >
        ({{ data.item.custom_price_change.diffPercentage }}%
        <font-awesome-icon class="text-muted" :icon="['fas', data.item.custom_price_change.icon]" :style="{ color: data.item.custom_price_change.color + '!important' }" />)
      </span>
    </template>
  </b-table>
</template>

<script>
export default {
  name: "MlsDataTable",
  props: {
    items: { type: Array, required: true },
    headerFields: { type: Array, required: true },
    preSort: { type: String, default: "" },
    sortCompare: { type: Function },
    tableReference: { type: String, default: "mlsTable" },
    filterFunction: { type: Function },
    filterOn: { type: Array, default: () => [] },
    filterRows: { type: String, default: "" },
    currentPage: { type: Number, default: 1 },
    perPage: { type: Number, default: 20 },
    rowClass: { type: Function },
    listingType: { type: String, default: "" },
    isAdmin: { type: Boolean, default: false },
    currentTab: { type: String, default: "" },
    currentUser: { type: Object, default: () => ({}) },
    userRole: { type: String, default: "" },
    openTab: { type: String, default: "" },
    showFlag: { type: Boolean, default: false },
    // Formatting/Logic methods passed from parent
    setColor: { type: Function, required: true },
    setConfidenceColor: { type: Function, required: true },
    isInCurrentQuarter: { type: Function, required: true },
    getDefaultValue: { type: Function, required: true },
    isMarketStrata: { type: Boolean, default: false }
  },
  data() {
    return {
      allRowsSelected: false,
    };
  },
  computed: {
    localPreSort: {
      get() { return this.preSort; },
      set(val) { this.$emit('update:preSort', val); }
    },
    filteredFields() {
      return this.headerFields.filter((header) => header.key !== 'custom_price_change');
    },
    isInternalRole() {
      return ['developer', 'developerbc', 'realtor'].includes(this.currentUser.role);
    },
    model_items:{
      get(){
        let data = this.items
        return this.openTab === 'unitmix' ? this.items : data.map(item => {
          return {
            ...item,
            custom_sold_psf: Number(item.mls_list_price / item.mls_unit_size).toFixed(2)
          }
        })
      },
      set(newValue) {
        return newValue
      }

    }
  },
  methods: {
    getPriceBackupKey(item) {
      return this.currentTab === 'projects' ? item.price_backup : item.price_back_up;
    },
    filterPlanningData(val) {
      return Object.fromEntries(
        Object.entries(val).filter(([k]) => ['REZ', 'DP'].includes(k))
      );
    },
    isSellingLeasingSoon(status) {
      return ['Selling Soon', 'Leasing Soon'].includes(status);
    },
    onFiltered(filteredItems) {
      this.$emit('filtered', filteredItems);
    }
  }
};
</script>
